import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ErrorStateComponent } from '../error-state/error-state.component';
import { LoadState } from '../../@services/data/load-state';

/** Au-delà, une phrase dit que l'app attend le réseau, pas qu'elle est figée. */
export const SLOW_LOADING_MS = 8000;

/**
 * Ce qu'un bloc montre tant que ses données ne sont pas là : le squelette
 * pendant le chargement (projeté avec l'attribut `skeleton`), puis la panne
 * dite selon sa nature, avec « Réessayer ». Prêt ou vide, il ne montre rien :
 * l'écran garde la main sur son contenu et son état vide, qui n'existe que
 * sur une réponse vide réussie. « Réessayer » repasse le bloc en chargement :
 * le squelette revient, la phrase sur le réseau lent aussi s'il le faut.
 *
 * Usage :
 * ```html
 * <vw-state [state]="state" (retry)="load()">
 *   <vw-skeleton-loader skeleton …></vw-skeleton-loader>
 * </vw-state>
 * ```
 */
@Component({
  selector: 'vw-state',
  standalone: true,
  imports: [TranslatePipe, ErrorStateComponent],
  templateUrl: './state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StateComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) state: LoadState;
  /** Titre propre à l'écran (clé i18n) ; sinon celui de la panne. `null` : pas de titre. */
  @Input() titleKey?: string | null;
  /** Message propre à l'écran (clé i18n) ; sinon celui de la panne. */
  @Input() messageKey?: string;
  /** Dans une carte ou une feuille : sans pictogramme ni grandes marges. */
  @Input({ transform: (value: boolean | string) => value !== false && value !== 'false' })
  compact = false;
  @Output() retry = new EventEmitter<void>();

  /** Le chargement dure : la phrase « le réseau est lent » s'affiche. */
  slow = false;
  private slowTimer?: ReturnType<typeof setTimeout>;
  private readonly zone = inject(NgZone);

  get errorTitleKey(): string | null {
    return this.titleKey !== undefined ? this.titleKey : `components.state.${this.state.error ?? 'server'}.title`;
  }

  get errorMessageKey(): string {
    return this.messageKey ?? `components.state.${this.state.error ?? 'server'}.message`;
  }

  ngOnChanges(): void {
    if (this.state?.status === 'loading') {
      // Hors de la zone : une phrase d'attente ne doit pas retenir la stabilité
      // de l'app (rendu serveur, tests, `whenStable`).
      this.slowTimer ??= this.zone.runOutsideAngular(() =>
        setTimeout(() => this.zone.run(() => (this.slow = true)), SLOW_LOADING_MS),
      );
    } else {
      this.stopSlowTimer();
    }
  }

  ngOnDestroy(): void {
    this.stopSlowTimer();
  }

  private stopSlowTimer(): void {
    clearTimeout(this.slowTimer);
    this.slowTimer = undefined;
    this.slow = false;
  }
}
